import type { SessionJourney, SessionMapSummary, SessionRecord } from '@/domain/types'
import { normalizeRomanNumerals } from '@/utils/roman-numerals'

export function normalizedSessionMap(value: string | null | undefined): string | null {
  return value?.trim() ? normalizeRomanNumerals(value.trim()) : null
}

/** Active-time accounting is independent of EXP availability and curve sampling. */
export class SessionMapTracker {
  private entries = new Map<string | null, SessionMapSummary['entries'][number]>()
  private map: string | null = null
  private cursor = 0

  reset(map: string | null = null): void {
    this.entries.clear(); this.map = normalizedSessionMap(map); this.cursor = 0
  }

  observe(activeAt: number, map: string | null): void {
    if (!Number.isFinite(activeAt) || activeAt < this.cursor) return
    this.entries = new Map(this.snapshot(activeAt).entries.map(entry => [entry.mapName, entry]))
    this.cursor = activeAt
    this.map = normalizedSessionMap(map)
  }

  snapshot(activeAt: number): SessionMapSummary {
    const entries = new Map(this.entries)
    const elapsed = Math.max(0, activeAt - this.cursor)
    if (elapsed > 0) {
      const previous = entries.get(this.map)
      entries.set(this.map, { mapName: this.map, activeDurationMs: (previous?.activeDurationMs ?? 0) + elapsed,
        firstSeenActiveMs: previous?.firstSeenActiveMs ?? this.cursor })
    }
    return { version: 1, source: 'observed', entries: [...entries.values()] }
  }
}

export function resolveSessionMap(record: SessionRecord): { name: string | null; basis: 'duration' | 'journey' | 'fallback' } {
  const entries = (record.mapSummary?.entries ?? []).map(e => ({ ...e, mapName: normalizedSessionMap(e.mapName) }))
    .filter(e => e.mapName && e.activeDurationMs > 0)
  const selected = [...entries].sort((a, b) => b.activeDurationMs - a.activeDurationMs
    || a.firstSeenActiveMs - b.firstSeenActiveMs || (a.mapName! < b.mapName! ? -1 : a.mapName === b.mapName ? 0 : 1))[0]
  return selected ? { name: selected.mapName, basis: record.mapSummary?.source === 'journey' ? 'journey' : 'duration' }
    : { name: normalizedSessionMap(record.startState?.mapName) ?? normalizedSessionMap(record.endState?.mapName), basis: 'fallback' }
}

/** Read-only projection: exports and stored original records are never rewritten. */
export function withJourneyMapSummary(record: SessionRecord, journey?: SessionJourney | null): SessionRecord {
  if (record.mapSummary || !journey || journey.sessionId !== record.id) return record
  const startMap = normalizedSessionMap(record.startState?.mapName)
  const events = journey.events.filter(e => e.type === 'map-change' && Number.isFinite(e.activeDurationMs))
    .map((event, index) => ({ event, index }))
    .sort((a, b) => a.event.activeDurationMs - b.event.activeDurationMs || a.event.capturedAt - b.event.capturedAt || a.index - b.index)
  if (!events.length && (!startMap || normalizedSessionMap(record.endState?.mapName) !== startMap)) return record
  const tracker = new SessionMapTracker()
  tracker.reset(startMap)
  for (const { event } of events) tracker.observe(Math.min(record.activeDurationMs, Math.max(0, event.activeDurationMs)), event.mapName ?? null)
  return { ...record, mapSummary: { ...tracker.snapshot(record.activeDurationMs), source: 'journey' } }
}

export async function hydrateSessionMaps(records: readonly SessionRecord[],
  load: (ids: string[]) => Promise<readonly (SessionJourney | undefined)[]>): Promise<SessionRecord[]> {
  const result = [...records]
  const missing = records.map((record, index) => ({ record, index })).filter(({ record }) => !record.mapSummary)
  for (let offset = 0; offset < missing.length; offset += 100) {
    const batch = missing.slice(offset, offset + 100)
    const journeys = await load(batch.map(({ record }) => record.id))
    batch.forEach(({ record, index }, i) => { result[index] = withJourneyMapSummary(record, journeys[i]) })
  }
  return result
}
